import React, { useState, useEffect, useRef } from 'react';
import './whitepaper.css';

// ─── Section Data ──────────────────────────────────────────────────────────

const SECTIONS = [
  {
    category: 'Getting Started',
    emoji: '🚀',
    items: [
      {
        id: 'introduction',
        title: 'Introduction',
        content: (
          <>
            <h1>Introduction</h1>
            <p>
              Distil Protocol is <strong>The Sanitation Department of Sui</strong> — a gamified wallet cleanup application that rewards users with <code>$DISTIL</code> tokens for removing spam, dust, and junk objects from their Sui wallets.
            </p>
            <p>
              The Sui blockchain is one of the fastest and most innovative Layer 1 networks in crypto. But like any thriving ecosystem, it accumulates waste: spam airdrops, zero-value dust tokens, failed transaction objects, and malicious assets designed to clutter your wallet and exploit your attention.
            </p>
            <h3>The Problem</h3>
            <ul>
              <li>Sui wallets accumulate hundreds of worthless objects over time</li>
              <li>Spam tokens and dust waste on-chain storage</li>
              <li>Users lose track of real assets among the junk</li>
              <li>Malicious objects can pose security risks</li>
              <li>No easy, rewarding way to clean up existed — until now</li>
            </ul>
            <h3>The Solution</h3>
            <p>
              Distil turns wallet cleanup into a rewarding game. Delete spam, earn <code>$DISTIL</code>, reclaim your SUI storage deposits, and climb the leaderboard — all while improving your wallet health and contributing to a cleaner Sui ecosystem.
            </p>
            <blockquote>
              "We're not just cleaning wallets. We're building the infrastructure for a healthier Sui ecosystem."
            </blockquote>
          </>
        ),
      },
      {
        id: 'what-is-distil',
        title: 'What is Distil?',
        content: (
          <>
            <h1>What is Distil?</h1>
            <p>
              Distil Protocol is a non-custodial, gamified wallet management tool built natively on Sui blockchain. It scans your wallet, identifies spam and dust objects, and rewards you for cleaning them up.
            </p>
            <h3>Core Loop</h3>
            <ol>
              <li><strong>Scan</strong> — Distil automatically identifies spam, dust, and junk in your wallet</li>
              <li><strong>Select</strong> — Choose which objects to purge (whitelist keeps valuables safe)</li>
              <li><strong>Purge</strong> — Execute a single transaction that deletes selected objects</li>
              <li><strong>Earn</strong> — Receive <code>$DISTIL</code> tokens + reclaim SUI storage deposits</li>
            </ol>
            <h3>Key Principles</h3>
            <ul>
              <li><strong>Non-custodial:</strong> You control your private keys at all times. Distil never has custody of your assets.</li>
              <li><strong>Earn while you clean:</strong> Every purge earns <code>$DISTIL</code> tokens proportional to the value and quantity of objects removed.</li>
              <li><strong>Reclaim SUI:</strong> Deleted objects return their storage deposits directly to your wallet — 100% goes to you.</li>
              <li><strong>Network health:</strong> Fewer junk objects means a faster, leaner Sui blockchain for everyone.</li>
            </ul>
          </>
        ),
      },
      {
        id: 'quick-start',
        title: 'Quick Start Guide',
        content: (
          <>
            <h1>Quick Start Guide</h1>
            <p>Get from zero to earning <code>$DISTIL</code> in under 2 minutes.</p>
            <div className="wp-steps">
              <div className="wp-step">
                <span className="wp-step-num">1</span>
                <div>
                  <strong>Connect Your Wallet</strong>
                  <p>Click "Connect" and select your wallet (Sui Wallet, Slush, or Phantom). Approval takes one click.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">2</span>
                <div>
                  <strong>View Your Objects</strong>
                  <p>Distil automatically scans and categorizes your wallet objects. Spam, dust, and junk are flagged for cleanup.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">3</span>
                <div>
                  <strong>Select & Protect</strong>
                  <p>Select objects to purge. Use the whitelist to protect anything you want to keep. Protected assets are automatically detected.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">4</span>
                <div>
                  <strong>Purge & Earn</strong>
                  <p>Click Purify. One transaction deletes everything selected and deposits <code>$DISTIL</code> rewards + reclaimed SUI directly to your wallet.</p>
                </div>
              </div>
            </div>
            <p>
              <strong>Not ready to connect?</strong> Use Demo Mode to explore all features with simulated wallet data — no connection required.
            </p>
          </>
        ),
      },
      {
        id: 'connect-wallet',
        title: 'Connect Your Wallet',
        content: (
          <>
            <h1>Connect Your Wallet</h1>
            <h3>Supported Wallets</h3>
            <ul>
              <li><strong>Sui Wallet</strong> — Official Mysten Labs wallet</li>
              <li><strong>Slush Wallet</strong> — Popular Sui ecosystem wallet</li>
              <li><strong>Phantom</strong> — Multi-chain wallet with Sui support</li>
            </ul>
            <h3>What's NOT Supported</h3>
            <p>
              <strong>zkLogin / Google Sign-In wallets are not supported.</strong> zkLogin wallets are non-exportable (no seed phrase), meaning you cannot import them into standard wallets. Since Distil requires full wallet control to execute purification transactions, zkLogin wallets are fundamentally incompatible.
            </p>
            <p>
              If you use zkLogin for other Sui apps (like Swayby), you'll need to connect a separate standard wallet for Distil.
            </p>
            <h3>Demo Mode</h3>
            <p>
              Don't want to connect a wallet yet? Demo Mode lets you explore the full Distil interface with simulated data. See how purification works, test the whitelist, check the leaderboard — all without any wallet connection or real transactions.
            </p>
            <h3>Security</h3>
            <p>
              Distil is <strong>100% non-custodial</strong>. The protocol never requests your private key or seed phrase. All transactions are signed locally in your wallet and broadcast directly to Sui network. Distil cannot move funds without your explicit approval for every transaction.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Core Features',
    emoji: '⚡',
    items: [
      {
        id: 'wallet-cleanup',
        title: 'Wallet Cleanup',
        content: (
          <>
            <h1>Wallet Cleanup</h1>
            <p>
              The core of Distil Protocol. An automatic scanner that identifies, categorizes, and helps you safely remove unwanted objects from your Sui wallet.
            </p>
            <h3>How Objects Are Categorized</h3>
            <ul>
              <li><strong>Dust:</strong> Tokens worth less than $0.01 USD — too small to be useful, but still taking up space and storage deposits</li>
              <li><strong>Zero-balance tokens:</strong> Coin objects with no actual balance — pure waste</li>
              <li><strong>Spam/Toxic:</strong> Unsolicited airdrop tokens, scam tokens, or objects with suspicious characteristics</li>
              <li><strong>Junk NFTs:</strong> Worthless NFT objects with no display value or utility</li>
            </ul>
            <h3>Auto-Protected Assets</h3>
            <p>
              Distil automatically identifies and protects high-value objects so you never accidentally delete something important:
            </p>
            <ul>
              <li>Your native SUI balance</li>
              <li>Tokens with real USD value</li>
              <li>LP positions and staking objects</li>
              <li>Distil Protocol objects</li>
              <li>Anything you've manually whitelisted</li>
            </ul>
            <h3>Batch Purification</h3>
            <p>
              Select multiple objects and delete them all in a single transaction. No per-object gas fees — one transaction, hundreds of objects purged, all storage deposits reclaimed at once.
            </p>
          </>
        ),
      },
      {
        id: 'purity-score',
        title: 'Purity Score System',
        content: (
          <>
            <h1>Purity Score System</h1>
            <p>
              Your Purity Score is a persistent measure of your total cleanup contributions. It grows with every purification and never decreases.
            </p>
            <h3>How Points Are Earned</h3>
            <p>
              Each purification earns points based on the quantity and value of objects removed. The formula accounts for both the number of objects purged and any bonus multipliers you have active.
            </p>
            <h3>What Purity Score Affects</h3>
            <ul>
              <li><strong>Badge Tier:</strong> Score thresholds unlock progressively powerful badges</li>
              <li><strong>Leaderboard Ranking:</strong> Global and weekly rankings are based on Purity Score</li>
              <li><strong>Achievement Unlocks:</strong> Milestone scores trigger special achievement badges</li>
              <li><strong>Community Status:</strong> Higher tiers are publicly visible on the leaderboard</li>
            </ul>
            <h3>Bonus Multipliers</h3>
            <p>Several factors can boost your point earnings:</p>
            <ul>
              <li>Daily streak bonuses (+5% per week, max +25%)</li>
              <li><code>$DISTIL</code> holding bonuses (Bronze through Diamond tiers)</li>
              <li>Toilet Mode bonus (+15% for <code>$TOILET</code> holders)</li>
            </ul>
          </>
        ),
      },
      {
        id: 'badges-tiers',
        title: 'Badges & Tiers',
        content: (
          <>
            <h1>Badges & Tiers</h1>
            <p>
              Eight progression tiers reward your commitment to wallet hygiene. Each tier unlocks a unique badge displayed on your profile and the leaderboard.
            </p>
            <div className="wp-tiers">
              {[
                { name: 'Novice', desc: 'Starting your cleanup journey' },
                { name: 'Apprentice', desc: 'Learning the art of purification' },
                { name: 'Adept', desc: 'Consistent cleanup habits forming' },
                { name: 'Expert', desc: 'Serious wallet hygiene practitioner' },
                { name: 'Master', desc: 'A force against wallet pollution' },
                { name: 'Elite', desc: 'Top-tier Distil contributor' },
                { name: 'Legendary', desc: 'Among the greatest cleaners of Sui' },
                { name: 'Mythic', desc: 'The pinnacle of purification mastery' },
              ].map((tier, i) => (
                <div key={tier.name} className="wp-tier-row">
                  <span className="wp-tier-num">{i + 1}</span>
                  <span className="wp-tier-name">{tier.name}</span>
                  <span className="wp-tier-desc">{tier.desc}</span>
                </div>
              ))}
            </div>
            <p>
              Tier advancement is based on cumulative Purity Score. Once you reach a tier, you keep it permanently.
            </p>
          </>
        ),
      },
      {
        id: 'daily-streaks',
        title: 'Daily Streaks',
        content: (
          <>
            <h1>Daily Streaks</h1>
            <p>
              Consistency is rewarded. Purify your wallet on consecutive days to build a streak and earn bonus rewards.
            </p>
            <h3>Streak Mechanics</h3>
            <ul>
              <li><strong>Building a streak:</strong> Complete at least one purification per day</li>
              <li><strong>Streak bonus:</strong> +5% reward boost per week of consecutive days, up to a maximum of +25%</li>
              <li><strong>Breaking a streak:</strong> Miss a day and your streak resets to zero</li>
              <li><strong>Milestone celebrations:</strong> Special animations and notifications for streak milestones</li>
            </ul>
            <h3>Viewing Your Streak</h3>
            <p>
              Your current streak and longest streak are displayed in the Stats panel. The leaderboard also shows streak data for top performers.
            </p>
            <h3>Strategy</h3>
            <p>
              Even a single small purification maintains your streak. You don't need to do a massive cleanup every day — just stay consistent to protect your bonus multiplier.
            </p>
          </>
        ),
      },
      {
        id: 'leaderboard',
        title: 'Leaderboard',
        content: (
          <>
            <h1>Leaderboard</h1>
            <p>
              Compete with the Sui community. The Distil leaderboard ranks the most active wallet cleaners globally.
            </p>
            <h3>Ranking Categories</h3>
            <ul>
              <li><strong>Daily:</strong> Top purifiers in the last 24 hours</li>
              <li><strong>Weekly:</strong> Top performers over the past 7 days</li>
              <li><strong>All-Time:</strong> Cumulative Purity Score rankings</li>
            </ul>
            <h3>What's Displayed</h3>
            <ul>
              <li>Wallet address (or SuiNS name when available)</li>
              <li>Current badge tier</li>
              <li>Purity Score</li>
              <li>Total objects cleaned</li>
              <li>Your current rank (always visible at bottom)</li>
            </ul>
            <p>
              Top 100 users are displayed. Your own rank is always shown regardless of position so you can track your progress relative to the community.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Tokenomics',
    emoji: '💎',
    items: [
      {
        id: 'distil-token',
        title: '$DISTIL Token',
        content: (
          <>
            <h1>$DISTIL Token</h1>
            <p>
              <code>$DISTIL</code> is the native utility and reward token of Distil Protocol, deployed on Sui blockchain.
            </p>
            <div className="wp-stat-grid">
              <div className="wp-stat">
                <span className="wp-stat-label">Total Supply</span>
                <span className="wp-stat-value">100,000,000</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Inflation</span>
                <span className="wp-stat-value">None</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Network</span>
                <span className="wp-stat-value">Sui</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Model</span>
                <span className="wp-stat-value">Deflationary</span>
              </div>
            </div>
            <h3>Token Utility</h3>
            <ul>
              <li><strong>Reward:</strong> Earned by purifying your wallet — the primary distribution mechanism</li>
              <li><strong>Holding bonuses:</strong> Hold <code>$DISTIL</code> to earn boosted rewards (no staking required)</li>
              <li><strong>Governance:</strong> Future DAO governance participation</li>
              <li><strong>Ecosystem:</strong> Access to Distillery launches, airdrops, and protocol benefits</li>
            </ul>
            <h3>Fixed Supply Forever</h3>
            <p>
              The 100M cap is immutable. No team can mint more tokens. Combined with the 1% burn per purge, the circulating supply will decrease over time as usage grows.
            </p>
          </>
        ),
      },
      {
        id: 'distribution',
        title: 'Distribution',
        content: (
          <>
            <h1>Token Distribution</h1>
            <p>Designed for fairness, liquidity, and long-term sustainability.</p>
            <div className="wp-distribution">
              {[
                { label: 'Fair Launch / Public Sale', pct: '50%', amount: '50M', color: '#22d3ee', desc: 'Available to anyone — no presale advantages' },
                { label: 'Liquidity Pool', pct: '15%', amount: '15M', color: '#a855f7', desc: 'Permanently locked — deep liquidity forever' },
                { label: 'Team', pct: '20%', amount: '20M', color: '#f59e0b', desc: '3-month cliff, then 27-month linear vesting' },
                { label: 'Ecosystem & Airdrops', pct: '10%', amount: '10M', color: '#10b981', desc: 'Community rewards, partnerships, Distillery' },
                { label: 'Strategic Reserve', pct: '5%', amount: '5M', color: '#6b7280', desc: 'Emergency fund and future opportunities' },
              ].map(item => (
                <div key={item.label} className="wp-dist-row">
                  <div className="wp-dist-bar-wrap">
                    <div className="wp-dist-bar" style={{ width: item.pct, background: item.color }} />
                  </div>
                  <div className="wp-dist-info">
                    <span className="wp-dist-label" style={{ color: item.color }}>{item.pct} — {item.label}</span>
                    <span className="wp-dist-amount">{item.amount} tokens</span>
                    <span className="wp-dist-desc">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'emission',
        title: 'Emission Schedule',
        content: (
          <>
            <h1>Emission Schedule</h1>
            <p>
              <code>$DISTIL</code> emissions are <strong>supply-based</strong>, not time-based. Rewards decrease as emission milestones are hit, not on a fixed calendar.
            </p>
            <div className="wp-phases">
              {[
                { phase: 'Phase 1', range: '0 – 2M emitted', reward: '10 $DISTIL / purge' },
                { phase: 'Phase 2', range: '2M – 4M emitted', reward: '9 $DISTIL / purge' },
                { phase: 'Phase 3', range: '4M – 6M emitted', reward: '8 $DISTIL / purge' },
                { phase: 'Phase 4', range: '6M – 8M emitted', reward: '7 $DISTIL / purge' },
                { phase: 'Phase 5+', range: 'Continues reducing...', reward: 'Decreasing over time' },
              ].map(p => (
                <div key={p.phase} className="wp-phase-row">
                  <span className="wp-phase-label">{p.phase}</span>
                  <span className="wp-phase-range">{p.range}</span>
                  <span className="wp-phase-reward">{p.reward}</span>
                </div>
              ))}
            </div>
            <h3>Why Supply-Based?</h3>
            <p>
              Traditional time-based emissions create predictable pump/dump cycles. Supply-based emissions are <strong>self-regulating</strong>:
            </p>
            <ul>
              <li><strong>Bull market:</strong> High activity → faster emission reduction → increased scarcity</li>
              <li><strong>Bear market:</strong> Lower activity → slower emission reduction → extended high rewards for active users</li>
            </ul>
            <p>
              This model is fairer to late adopters — a slow launch means extended high rewards, not a disadvantage.
            </p>
          </>
        ),
      },
      {
        id: 'holding-bonuses',
        title: 'Holding Bonuses',
        content: (
          <>
            <h1>Holding Bonuses</h1>
            <p>
              <strong>No staking. No lockups. No risk.</strong> Simply holding <code>$DISTIL</code> in your wallet earns you boosted rewards automatically.
            </p>
            <div className="wp-tiers">
              {[
                { tier: '🥉 Bronze', req: '1,000+ $DISTIL', bonus: '+5% rewards', color: '#cd7f32' },
                { tier: '🥈 Silver', req: '10,000+ $DISTIL', bonus: '+10% rewards', color: '#c0c0c0' },
                { tier: '🏆 Gold', req: '100,000+ $DISTIL', bonus: '+15% rewards', color: '#ffd700' },
                { tier: '💎 Diamond', req: '1,000,000+ $DISTIL', bonus: '+20% rewards + revenue share', color: '#22d3ee' },
              ].map(t => (
                <div key={t.tier} className="wp-tier-row">
                  <span className="wp-tier-name" style={{ color: t.color }}>{t.tier}</span>
                  <span className="wp-tier-desc">{t.req}</span>
                  <span className="wp-tier-bonus" style={{ color: t.color }}>{t.bonus}</span>
                </div>
              ))}
            </div>
            <p>
              Bonuses stack with streak bonuses and Toilet Mode bonuses. Diamond holders also receive a share of protocol revenue.
            </p>
            <h3>How It Works</h3>
            <p>
              Your wallet balance is checked at the time of each purification. The appropriate bonus tier is applied automatically — no action required on your part.
            </p>
          </>
        ),
      },
      {
        id: 'burn-mechanics',
        title: 'Burn Mechanics',
        content: (
          <>
            <h1>Burn Mechanics</h1>
            <p>
              Every purification permanently removes <code>$DISTIL</code> from circulation. This creates ongoing deflationary pressure as the protocol scales.
            </p>
            <h3>Reward Distribution Per Purge</h3>
            <div className="wp-burn-split">
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '92%', background: '#22d3ee' }} />
                <span className="wp-burn-label"><strong>92%</strong> → You (the user)</span>
              </div>
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '7%', background: '#a855f7' }} />
                <span className="wp-burn-label"><strong>7%</strong> → Protocol operations</span>
              </div>
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '1%', background: '#ef4444' }} />
                <span className="wp-burn-label"><strong>1%</strong> → 🔥 Burned forever</span>
              </div>
            </div>
            <h3>Why Burn?</h3>
            <p>
              With a fixed 100M supply and 1% burned per purge, every transaction reduces circulating supply. As Distil grows:
            </p>
            <ul>
              <li>More purifications = more burns</li>
              <li>More burns = lower circulating supply</li>
              <li>Lower supply + steady demand = increased scarcity</li>
            </ul>
            <p>
              This mechanism is inspired by BNB's buyback-and-burn model, applied at the transaction level — automatic, trustless, and continuous.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'The Distillery',
    emoji: '🏭',
    items: [
      {
        id: 'distillery-overview',
        title: 'Overview',
        content: (
          <>
            <h1>The Distillery</h1>
            <p>
              The Distillery is Distil's <strong>curated meme token launch platform</strong> — a home for quality-first token launches on Sui with fair distribution and community involvement.
            </p>
            <p>
              Unlike permissionless launchpads flooded with low-effort tokens, The Distillery applies a premium curation model. Every launch goes through a vetting process. The result: a higher signal-to-noise ratio and better outcomes for participants.
            </p>
            <h3>Philosophy</h3>
            <blockquote>
              Quality over quantity. The Distillery launches fewer tokens than competing platforms — but every launch is intentional, vetted, and backed by community conviction.
            </blockquote>
          </>
        ),
      },
      {
        id: 'how-it-works',
        title: 'How It Works',
        content: (
          <>
            <h1>How The Distillery Works</h1>

            {/* ================================================================
                🔓 REVEAL WHEN READY — swap the wp-coming-soon block below
                with this commented block to show full Distillery details.
                Just delete the wp-coming-soon div and uncomment this section.
                ================================================================

            <ul>
              <li><strong>Curation-first:</strong> Projects are vetted before launch — no permissionless spam</li>
              <li><strong>Fair launches:</strong> Bonding curve mechanics via MoonBags.io ensure fair price discovery</li>
              <li><strong>Community participation:</strong> $DISTIL holders get priority access and airdrop eligibility</li>
              <li><strong>Liquidity engine:</strong> 60% of fees flow to liquidity, 30% to $DISTIL holders, 10% burned</li>
            </ul>
            <h3>Prompt.fun Integration</h3>
            <p>
              Prompt.fun (launching Month 3 of Meme Season) is an AI-powered meme curation platform
              integrated with The Distillery. Users submit prompts, Claude AI generates concepts,
              the community votes, and winning concepts launch as Distillery tokens.
            </p>
            <p>
              Voting power is weighted: base users 1x, Distil users 2x, $DISTIL holders 3x, Toilet Mode holders 4x.
            </p>

            ================================================================ */}

            <div className="wp-coming-soon">
              <span className="wp-coming-soon-icon">🔒</span>
              <div>
                <strong>Full details coming soon</strong>
                <p>
                  The complete Distillery mechanics, launch process, and ecosystem integrations are being finalized.
                  Full documentation will be published ahead of the first launch.
                </p>
                <p>
                  What we can share: <code>$DISTIL</code> holders will receive priority access and airdrop eligibility for every Distillery launch. Hold <code>$DISTIL</code> to be ready.
                </p>
              </div>
            </div>
          </>
        ),
      },
      {
        id: 'airdrops',
        title: 'Airdrops for Distil Users',
        content: (
          <>
            <h1>Airdrops for Distil Users</h1>
            <p>
              Holding <code>$DISTIL</code> is your ticket to Distillery airdrop eligibility. Every token launched through The Distillery includes an allocation for <code>$DISTIL</code> holders.
            </p>
            <ul>
              <li>Hold <code>$DISTIL</code> in your wallet — no staking required</li>
              <li>Snapshots taken at each launch time</li>
              <li>Distribution proportional to your holdings</li>
              <li>Tokens delivered directly to your wallet</li>
            </ul>

            {/* ================================================================
                🔓 REVEAL WHEN READY — delete the wp-coming-soon block below
                and uncomment this section to show full airdrop details.
                ================================================================

            <h3>Why This Matters</h3>
            <p>
              Each Distillery launch creates a new revenue stream for long-term $DISTIL holders.
              As more tokens launch through the platform, the value of holding $DISTIL compounds —
              you get exposure across the entire Sui meme ecosystem.
            </p>

            ================================================================ */}

            <div className="wp-coming-soon">
              <span className="wp-coming-soon-icon">⏳</span>
              <div>
                <strong>More details coming soon</strong>
                <p>Additional Distillery launch mechanics and ecosystem integrations will be announced as launches approach. Follow official channels to stay updated.</p>
              </div>
            </div>
          </>
        ),
      },
    ],
  },
  {
    category: 'Toilet Mode 🚽',
    emoji: '🚽',
    items: [
      {
        id: 'toilet-mode-what',
        title: 'What is Toilet Mode?',
        content: (
          <>
            <h1>🚽 What is Toilet Mode?</h1>
            <p>
              Toilet Mode is Distil's <strong>premium tier experience</strong>, powered by the <code>$TOILET</code> token partnership. It's a visual and mechanical upgrade that rewards serious cleaners with enhanced earnings and exclusive features.
            </p>
            <blockquote>
              "PURGE THE FILTH, GET RICH, FLEX ON PEASANTS"
            </blockquote>
            <h3>What Changes in Toilet Mode</h3>
            <ul>
              <li><strong>Visual:</strong> The interface transforms into a Dark Cosmic theme — deep blacks, teal accents, enhanced animations</li>
              <li><strong>Earnings:</strong> 15% bonus on all <code>$DISTIL</code> rewards (stacks with other bonuses)</li>
              <li><strong>Gas:</strong> <code>$DISTIL</code> Gas Rebate on purge transactions</li>
              <li><strong>Exclusives:</strong> SpinWheel mini-game, Toilet Warrior badge, priority leaderboard highlighting</li>
            </ul>
          </>
        ),
      },
      {
        id: 'toilet-partnership',
        title: 'Partnership & Requirements',
        content: (
          <>
            <h1>Partnership & Requirements</h1>
            <h3>The $TOILET Partnership</h3>
            <p>
              Toilet Mode is powered by a cross-token partnership with the <code>$TOILET</code> community on Sui (flush.wtf). This demonstrates Distil's commitment to building real utility across the Sui ecosystem rather than operating in isolation.
            </p>
            <h3>Unlock Requirements</h3>
            <ul>
              <li><strong>Token:</strong> <code>$TOILET</code></li>
              <li><strong>Minimum:</strong> 287,000 <code>$TOILET</code> tokens</li>
              <li><strong>Approximate cost:</strong> ~$10 USD (at time of writing)</li>
              <li><strong>Detection:</strong> Automatic — just connect a wallet holding the required amount</li>
            </ul>
            <h3>Cross-Token Utility Model</h3>
            <p>
              The <code>$TOILET</code> partnership establishes a template for future cross-protocol utility on Sui. Holding a partner token unlocks benefits in Distil — creating mutual ecosystem value and deeper token utility for both communities.
            </p>
          </>
        ),
      },
      {
        id: 'toilet-benefits',
        title: 'Benefits Summary',
        content: (
          <>
            <h1>Toilet Mode Benefits</h1>
            <div className="wp-benefits">
              {[
                { icon: '💰', title: '15% Bonus Earnings', desc: 'All $DISTIL rewards boosted 15%. Stacks with streak and holding bonuses.' },
                { icon: '⛽', title: '$DISTIL Gas Rebate', desc: 'Gas costs partially offset via $DISTIL rebate on every purge transaction.' },
                { icon: '🎨', title: 'Dark Cosmic Theme', desc: 'Interface transforms to an immersive dark cosmic visual experience.' },
                { icon: '🏆', title: 'Toilet Warrior Badge', desc: 'Exclusive community status badge visible on leaderboard.' },
                { icon: '🎮', title: 'All Mini-Games Access', desc: 'SpinWheel (daily spin up to +100 $DISTIL), Flush Frenzy (timed purge challenge), and Scratch-Off (milestone reward randomizer) — all Toilet Mode exclusive.' },
                { icon: '📊', title: 'Leaderboard Priority', desc: 'Toilet Mode users highlighted on the community leaderboard.' },
              ].map(b => (
                <div key={b.title} className="wp-benefit-card">
                  <span className="wp-benefit-icon">{b.icon}</span>
                  <div>
                    <strong>{b.title}</strong>
                    <p>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'toilet-activate',
        title: 'How to Activate',
        content: (
          <>
            <h1>How to Activate Toilet Mode</h1>
            <div className="wp-steps">
              <div className="wp-step">
                <span className="wp-step-num">1</span>
                <div>
                  <strong>Acquire $TOILET</strong>
                  <p>Purchase 287,000+ <code>$TOILET</code> tokens on any Sui DEX. At current prices, this costs approximately $10 USD. Visit flush.wtf for links.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">2</span>
                <div>
                  <strong>Connect Your Wallet</strong>
                  <p>Connect the wallet that holds your <code>$TOILET</code> balance to Distil.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">3</span>
                <div>
                  <strong>Auto-Detection</strong>
                  <p>Distil automatically detects your <code>$TOILET</code> balance. If the threshold is met, Toilet Mode activates immediately — no manual action needed.</p>
                </div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">4</span>
                <div>
                  <strong>Enjoy the Benefits</strong>
                  <p>All Toilet Mode perks apply immediately to your next purification. The 15% bonus applies to every purge while you hold the required balance.</p>
                </div>
              </div>
            </div>
          </>
        ),
      },
    ],
  },
  {
    category: 'Advanced Features',
    emoji: '🔧',
    items: [
      {
        id: 'whitelist',
        title: 'Whitelist System',
        content: (
          <>
            <h1>Whitelist System</h1>
            <p>
              The Whitelist is your personal safe list — objects here will never appear in the cleanup list and cannot be accidentally purged.
            </p>
            <h3>How to Whitelist</h3>
            <ul>
              <li>Click the <strong>(⋮) menu</strong> on any object in the cleanup list</li>
              <li>Select <strong>Whitelist</strong> to protect it immediately</li>
              <li>The object disappears from the cleanup list and appears in the Whitelist Manager</li>
            </ul>
            <h3>Whitelist Manager</h3>
            <p>
              Access the full whitelist via the <strong>Whitelist</strong> button in the header. Here you can:
            </p>
            <ul>
              <li>View all whitelisted objects with their names, icons, and balances</li>
              <li>Remove objects from the whitelist (they return to the cleanup list immediately)</li>
              <li>Manually add object IDs (admin feature)</li>
            </ul>
            <h3>Cross-Device Sync</h3>
            <p>
              Your whitelist syncs automatically to the cloud (Supabase) whenever you connect a wallet. Reconnecting on a new device restores your whitelist instantly.
            </p>
          </>
        ),
      },
      {
        id: 'protected-assets',
        title: 'Protected Assets',
        content: (
          <>
            <h1>Protected Assets</h1>
            <p>
              Beyond the manual whitelist, Distil automatically protects high-value assets so you never have to worry about accidental deletions.
            </p>
            <h3>What's Auto-Protected</h3>
            <ul>
              <li><strong>Native SUI</strong> — Your SUI balance is always protected</li>
              <li><strong>Tokens with value</strong> — Any token with USD value above a threshold</li>
              <li><strong>LP tokens</strong> — Liquidity pool positions</li>
              <li><strong>Staking positions</strong> — Staked assets and related objects</li>
              <li><strong>Distil Protocol objects</strong> — Your badges and protocol-related objects</li>
            </ul>
            <h3>Viewing Protected Assets</h3>
            <p>
              Click the <strong>Protected</strong> button in the dashboard to see a full breakdown of all auto-protected assets and their estimated values. This is a read-only view — management happens through the Whitelist panel.
            </p>
          </>
        ),
      },
      {
        id: 'quarantine',
        title: 'Quarantine System',
        content: (
          <>
            <h1>Quarantine System</h1>
            <p>
              Some objects on Sui are actively malicious — they contain code designed to steal funds when interacted with. Distil's Quarantine System isolates these objects automatically.
            </p>
            <h3>How Quarantine Works</h3>
            <ul>
              <li>If your wallet's security layer (e.g. Slush Wallet) flags an object as malicious, Distil intercepts the error</li>
              <li>The object is moved to a separate Quarantine list rather than appearing in the cleanup list</li>
              <li>Quarantined objects <strong>cannot be deleted through Distil</strong> — they must be reported to your wallet provider</li>
              <li>Quarantine persists across sessions and devices via Supabase</li>
            </ul>
            <h3>False Positives</h3>
            <p>
              If an object is incorrectly quarantined, you can use the <strong>"Return to Cleanup List"</strong> button in the Quarantine panel to restore it. This is a per-object action requiring deliberate intent.
            </p>
            <h3>Reporting Malicious Objects</h3>
            <p>
              Report quarantined objects to <strong>support@mystenlabs.com</strong> (Slush Wallet support) with the object ID for investigation.
            </p>
          </>
        ),
      },
      {
        id: 'mini-games',
        title: 'Mini-Games',
        content: (
          <>
            <h1>Mini-Games</h1>
            <p>
              All Distil mini-games are <strong>Toilet Mode exclusive</strong> — a core benefit of holding <code>$TOILET</code> tokens. Mini-games provide additional ways to earn <code>$DISTIL</code> and keep engagement high between purges.
            </p>
            <div className="wp-benefit-card" style={{ marginBottom: '1rem' }}>
              <span className="wp-benefit-icon">🎰</span>
              <div>
                <strong>SpinWheel</strong> <span className="wp-tag">Toilet Mode Exclusive</span>
                <p>Daily luck-based spin for bonus <code>$DISTIL</code> rewards. Up to +100 <code>$DISTIL</code> per day. Resets every 24 hours.</p>
              </div>
            </div>
            <div className="wp-benefit-card" style={{ marginBottom: '1rem' }}>
              <span className="wp-benefit-icon">⚡</span>
              <div>
                <strong>Flush Frenzy</strong> <span className="wp-tag">Toilet Mode Exclusive</span>
                <p>Timed purge challenge. Select and purge as many objects as possible before the timer runs out. Bonus rewards for high-volume runs.</p>
              </div>
            </div>
            <div className="wp-benefit-card" style={{ marginBottom: '1rem' }}>
              <span className="wp-benefit-icon">🎟️</span>
              <div>
                <strong>Scratch-Off</strong> <span className="wp-tag">Toilet Mode Exclusive</span>
                <p>Reward randomizer mechanic. Earn scratch cards through purification milestones and reveal bonus <code>$DISTIL</code> prizes.</p>
              </div>
            </div>
            <p>Additional mini-games are planned for future updates — all will be Toilet Mode exclusive.</p>
          </>
        ),
      },
      {
        id: 'referral-system',
        title: 'Referral System',
        content: (
          <>
            <h1>Referral System</h1>
            <p>
              Distil's referral system rewards users for growing the community. Share your unique referral link — when a friend qualifies, both of you earn <code>$DISTIL</code>.
            </p>
            <h3>How It Works</h3>
            <div className="wp-steps">
              <div className="wp-step">
                <span className="wp-step-num">1</span>
                <div><strong>Generate your link</strong><p>Click the Invite button in the header to get your unique referral URL.</p></div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">2</span>
                <div><strong>Share it</strong><p>Share via Twitter, Telegram, Discord, or direct link. Pre-filled messages make sharing frictionless.</p></div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">3</span>
                <div><strong>Friend qualifies</strong><p>Your friend connects their wallet and earns 500+ purity points through active cleanup.</p></div>
              </div>
              <div className="wp-step">
                <span className="wp-step-num">4</span>
                <div><strong>Both rewarded</strong><p>You earn 100 $DISTIL. They get 50 $DISTIL welcome bonus. Rewards distributed at TGE.</p></div>
              </div>
            </div>
            <h3>Referral Tier Bonuses</h3>
            <p>Build your referral count to unlock a permanent earnings bonus that stacks with all other bonuses:</p>
            <div className="wp-tiers">
              {[
                { name: '🤝 Introducer', req: '1–9 referrals', bonus: '+5%' },
                { name: '⚡ Recruiter',  req: '10–24 referrals', bonus: '+10%' },
                { name: '🔥 Evangelist', req: '25–49 referrals', bonus: '+15%' },
                { name: '🌟 Ambassador', req: '50–99 referrals', bonus: '+20%' },
                { name: '👑 Legend',     req: '100+ referrals', bonus: '+25%' },
              ].map(t => (
                <div key={t.name} className="wp-tier-row">
                  <span className="wp-tier-name">{t.name}</span>
                  <span className="wp-tier-desc">{t.req}</span>
                  <span className="wp-tier-bonus" style={{ color: '#22d3ee' }}>{t.bonus} all earnings</span>
                </div>
              ))}
            </div>
            <p>Referral tier bonuses stack with holding bonuses, streak bonuses, and Toilet Mode bonuses.</p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Economics',
    emoji: '📊',
    items: [
      {
        id: 'deflationary',
        title: 'Deflationary Model',
        content: (
          <>
            <h1>Deflationary Model</h1>
            <p>
              <code>$DISTIL</code> is built with multi-layered scarcity mechanisms that compound over time.
            </p>
            <h3>The Four Layers</h3>
            <ol>
              <li><strong>Fixed Supply:</strong> 100M tokens, no minting ever. Total supply can only decrease.</li>
              <li><strong>Per-Purge Burns:</strong> 1% of every reward is permanently burned. Daily burns accumulate to meaningful supply reduction at scale.</li>
              <li><strong>Holding Incentives:</strong> Bonuses for holding discourage selling, reducing effective circulating supply over time.</li>
              <li><strong>Emission Reduction:</strong> Fewer tokens distributed per purge as milestones are hit. Early participants earn more than late participants.</li>
            </ol>
            <h3>Inspired by Proven Models</h3>
            <p>
              Distil combines Bitcoin's scarcity (fixed supply), BNB's burns (deflationary mechanics), and a novel supply-based emission schedule — adapted for a utility-first protocol.
            </p>
          </>
        ),
      },
      {
        id: 'storage-fees',
        title: 'Storage Fee Recovery',
        content: (
          <>
            <h1>Storage Fee Recovery</h1>
            <p>
              This is one of Distil's most underrated features. Every object on the Sui blockchain consumes on-chain storage, and Sui charges a <strong>storage deposit</strong> when objects are created.
            </p>
            <h3>How It Works</h3>
            <ul>
              <li>When a spam token is airdropped to your wallet, it locks a small SUI storage deposit</li>
              <li>You paid nothing for this — the spammer paid the deposit — but it's <em>your SUI</em> now locked in junk</li>
              <li>Deleting the object returns that storage deposit to your wallet</li>
              <li>Distil recovers these deposits for you automatically during purification</li>
            </ul>
            <h3>You Keep 100%</h3>
            <p>
              Unlike some protocols that take a fee on storage recovery, Distil returns the full SUI storage deposit to you. You earn both <code>$DISTIL</code> rewards <em>and</em> recovered SUI from the same transaction.
            </p>
            <p>
              On a large wallet cleanup, the recovered SUI can be substantial — sometimes more valuable than the <code>$DISTIL</code> rewards themselves.
            </p>
          </>
        ),
      },
      {
        id: 'protocol-revenue',
        title: 'Protocol Revenue',
        content: (
          <>
            <h1>Protocol Revenue</h1>
            <p>
              7% of each purge reward funds Distil Protocol operations. This creates a sustainable, self-funding business model that doesn't depend on VC funding or token sales.
            </p>
            <h3>Use of Funds</h3>
            <ul>
              <li><strong>Development:</strong> Ongoing protocol improvements, new features, and infrastructure</li>
              <li><strong>Security:</strong> Ongoing security audits and bug bounty programs</li>
              <li><strong>Infrastructure:</strong> RPC nodes, cloud services, and uptime maintenance</li>
              <li><strong>Marketing:</strong> Community growth, partnerships, and ecosystem expansion</li>
            </ul>
            <h3>Transparency</h3>
            <p>
              All protocol revenue flows are on-chain and publicly verifiable. The 7% split is hardcoded in the smart contract and cannot be changed without a governance vote.
            </p>
          </>
        ),
      },
      {
        id: 'team-vesting',
        title: 'Team Vesting',
        content: (
          <>
            <h1>Team Vesting</h1>
            <p>
              Team alignment is enforced by the vesting schedule. No team member can access their allocation before the cliff expires.
            </p>
            <div className="wp-stat-grid">
              <div className="wp-stat">
                <span className="wp-stat-label">Total Allocation</span>
                <span className="wp-stat-value">20M tokens (20%)</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Cliff Period</span>
                <span className="wp-stat-value">3 months</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Vesting Duration</span>
                <span className="wp-stat-value">27 months linear</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Monthly Release</span>
                <span className="wp-stat-value">~679,000 tokens</span>
              </div>
            </div>
            <p>
              This schedule ensures that the team's incentives are aligned with long-term protocol success. There are no early dump opportunities — the team benefits only as the protocol grows.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Three-Engine System',
    emoji: '⚙️',
    items: [
      {
        id: 'three-engine-overview',
        title: 'Overview',
        content: (
          <>
            <h1>The Three-Engine Tokenomics System</h1>
            <p>
              Distil Protocol introduces a three-engine tokenomics architecture that creates sustainable, self-reinforcing liquidity growth. Unlike traditional DeFi protocols that rely on perpetual incentive emissions or unsustainable yield farming, our system combines three interconnected engines that work together to create a regenerative flywheel effect.
            </p>
            <p>
              This makes Distil one of the first protocols anywhere to successfully integrate fair launch mechanics, regenerative liquidity, and market-adaptive token economics into a unified, self-sustaining system.
            </p>
            <div className="wp-engine-cards">
              {[
                { emoji: '🌊', name: 'Gravity Pools', role: 'Fair Launch Bootstrap', timeline: 'Aug–Nov 2026' },
                { emoji: '⚙️', name: 'Liquidity Engine', role: 'Regenerative Growth', timeline: 'Nov 2026+' },
                { emoji: '📡', name: 'Sui Pulse', role: 'Market-Adaptive Oracle', timeline: 'Nov 2026+' },
              ].map(e => (
                <div key={e.name} className="wp-engine-card">
                  <span className="wp-engine-emoji">{e.emoji}</span>
                  <div>
                    <strong>{e.name}</strong>
                    <span className="wp-engine-role">{e.role}</span>
                    <span className="wp-engine-timeline">{e.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'gravity-pools',
        title: 'Engine #1: Gravity Pools',
        content: (
          <>
            <h1>🌊 Engine #1: Gravity Pools</h1>
            <p>
              Gravity Pools is our fair launch mechanism running for 90 days (August 15 – November 15, 2026) leading up to the Token Generation Event. Unlike traditional liquidity mining that favors whales, Gravity Pools rewards early commitment and conviction — not just capital size.
            </p>
            <h3>How It Works</h3>
            <ul>
              <li>Users stake SUI or SUI/USDC LP tokens into the pool</li>
              <li>Earn gravity points based on deposit amount × time commitment weight</li>
              <li>Early depositors receive higher time-weight multipliers via a decay curve</li>
              <li>Top leaderboard positions earn permanent fee revenue multipliers</li>
              <li>All participants receive guaranteed <code>$DISTIL</code> allocation at TGE</li>
            </ul>
            <h3>Commitment Tiers</h3>
            <div className="wp-phases">
              <div className="wp-phase-row">
                <span className="wp-phase-label">30-day lock</span>
                <span className="wp-phase-range">Standard commitment</span>
                <span className="wp-phase-reward">1.2× multiplier</span>
              </div>
              <div className="wp-phase-row">
                <span className="wp-phase-label">60-day lock</span>
                <span className="wp-phase-range">Strong commitment</span>
                <span className="wp-phase-reward">1.5× multiplier</span>
              </div>
              <div className="wp-phase-row">
                <span className="wp-phase-label">90-day lock</span>
                <span className="wp-phase-range">Maximum conviction</span>
                <span className="wp-phase-reward">2.0× multiplier</span>
              </div>
            </div>
            <h3>Key Innovation</h3>
            <p>
              Unlike traditional fair launches that get dumped immediately, Gravity Pools creates a committed holder base with aligned long-term incentives. Top contributors earn <strong>permanent</strong> revenue share multipliers — creating a loyal LP base that benefits from protocol growth forever.
            </p>
            <div className="wp-stat-grid">
              <div className="wp-stat">
                <span className="wp-stat-label">Opens</span>
                <span className="wp-stat-value">Aug 15, 2026</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Closes</span>
                <span className="wp-stat-value">Nov 15, 2026</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">TGE</span>
                <span className="wp-stat-value">Nov 30, 2026</span>
              </div>
              <div className="wp-stat">
                <span className="wp-stat-label">Multipliers</span>
                <span className="wp-stat-value">Permanent</span>
              </div>
            </div>
          </>
        ),
      },
      {
        id: 'liquidity-engine',
        title: 'Engine #2: Liquidity Engine',
        content: (
          <>
            <h1>⚙️ Engine #2: Liquidity Engine</h1>
            <p>
              The Liquidity Engine is a regenerative liquidity mechanism that automatically reinvests trading fees back into the liquidity pool, creating compounding depth without requiring new capital inflows. This engine activates at TGE and runs perpetually.
            </p>
            <h3>Fee Distribution (Every Swap)</h3>
            <div className="wp-burn-split">
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '60%', background: '#22d3ee' }} />
                <span className="wp-burn-label"><strong>60%</strong> → Reinvested directly into liquidity pool (auto-compounds)</span>
              </div>
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '30%', background: '#a855f7' }} />
                <span className="wp-burn-label"><strong>30%</strong> → Distributed to LP token holders</span>
              </div>
              <div className="wp-burn-row">
                <div className="wp-burn-bar" style={{ width: '10%', background: '#ef4444' }} />
                <span className="wp-burn-label"><strong>10%</strong> → 🔥 Burned permanently</span>
              </div>
            </div>
            <h3>The Regenerative Effect</h3>
            <p>
              Most DeFi pools stagnate or shrink over time as LPs remove liquidity. Distil's pool grows automatically from organic trading activity. Better prices attract more traders, generating more fees, which accelerates growth — a self-sustaining flywheel that benefits all participants.
            </p>
            <h3>Technical Implementation</h3>
            <ul>
              <li>Built on Cetus 0.3% concentrated liquidity pools</li>
              <li>Auto-compound mechanism via smart contract hooks</li>
              <li>Transparent on-chain tracking — anyone can verify</li>
              <li>Governance-adjustable parameters via DAO post-launch</li>
            </ul>
          </>
        ),
      },
      {
        id: 'sui-pulse',
        title: 'Engine #3: Sui Pulse',
        content: (
          <>
            <h1>📡 Engine #3: Sui Pulse</h1>
            <p>
              Sui Pulse is an on-chain market health oracle that measures ecosystem vitality on a 0–100 scale and dynamically adjusts token burn rates based on market conditions. This creates adaptive deflationary pressure that prevents death spirals in bear markets while amplifying burns during bull runs.
            </p>
            <h3>How Sui Pulse Works</h3>
            <p>The oracle monitors 5 key Sui ecosystem metrics:</p>
            <ul>
              <li>Total Value Locked (TVL) across major protocols</li>
              <li>Daily transaction volume</li>
              <li>New wallet activations (growth signal)</li>
              <li>Stablecoin inflows/outflows (capital flows)</li>
              <li>Blue-chip token price performance</li>
            </ul>
            <p>These metrics are weighted into a single 0–100 Pulse Score that updates every 24 hours.</p>
            <h3>Dynamic Burn Mechanism</h3>
            <div className="wp-phases">
              {[
                { label: 'Score 0–20',  range: 'Deep Bear',    reward: '0.2% burn (preserve liquidity)' },
                { label: 'Score 21–40', range: 'Bear',         reward: '0.5% burn' },
                { label: 'Score 41–60', range: 'Neutral',      reward: '1.0% burn (baseline)' },
                { label: 'Score 61–80', range: 'Bull',         reward: '2.0% burn' },
                { label: 'Score 81–100',range: 'Extreme Bull', reward: '3.0% burn (max deflation)' },
              ].map(p => (
                <div key={p.label} className="wp-phase-row">
                  <span className="wp-phase-label">{p.label}</span>
                  <span className="wp-phase-range">{p.range}</span>
                  <span className="wp-phase-reward">{p.reward}</span>
                </div>
              ))}
            </div>
            <h3>Why This Is Different</h3>
            <p>Most tokens have fixed burn rates that hurt liquidity in bear markets and miss opportunity in bull markets. Sui Pulse adapts in real-time:</p>
            <ul>
              <li><strong>Bear markets:</strong> Low burns preserve liquidity, prevent death spirals</li>
              <li><strong>Bull markets:</strong> High burns create scarcity, amplify price appreciation</li>
              <li><strong>Neutral:</strong> Baseline burns maintain steady deflation</li>
            </ul>
            <h3>Technical Implementation</h3>
            <ul>
              <li>On-chain oracle — no centralized price feeds</li>
              <li>24-hour update cycle (prevents manipulation)</li>
              <li>Community-auditable formula with transparent weighting</li>
              <li>Governance-adjustable via DAO vote if needed</li>
            </ul>
          </>
        ),
      },
      {
        id: 'flywheel',
        title: 'The Flywheel Effect',
        content: (
          <>
            <h1>The Flywheel Effect</h1>
            <p>
              The three engines don't operate independently — they reinforce each other to create a compounding system that strengthens over time.
            </p>
            <div className="wp-flywheel">
              <div className="wp-flywheel-step">
                <span className="wp-flywheel-num">1</span>
                <div>
                  <strong>Gravity Pools</strong> creates a committed LP base (Aug–Nov 2026) with aligned long-term incentives and guaranteed launch liquidity.
                </div>
              </div>
              <div className="wp-flywheel-arrow">↓</div>
              <div className="wp-flywheel-step">
                <span className="wp-flywheel-num">2</span>
                <div>
                  <strong>Liquidity Engine</strong> grows the pool automatically from trading fees — 60% reinvested forever, compounding depth without new capital.
                </div>
              </div>
              <div className="wp-flywheel-arrow">↓</div>
              <div className="wp-flywheel-step">
                <span className="wp-flywheel-num">3</span>
                <div>
                  <strong>Sui Pulse</strong> protects and amplifies — reducing burns in bear markets to preserve liquidity, increasing burns in bull markets to create scarcity.
                </div>
              </div>
              <div className="wp-flywheel-arrow">↓</div>
              <div className="wp-flywheel-result">
                ♻️ Self-sustaining, adaptive, regenerative DeFi system
              </div>
            </div>
            <h3>The Core Loop</h3>
            <p>
              More users → more purifications → more <code>$DISTIL</code> trading volume → more fees collected → Liquidity Engine grows the pool → better prices and deeper liquidity → more users. Meanwhile Sui Pulse adapts burns to market conditions and Gravity Pool LPs earn permanent multipliers. The system strengthens itself over time.
            </p>
            <h3>Why This Is Category-Defining</h3>
            <div className="wp-compare-table">
              <div className="wp-compare-row wp-compare-header">
                <span>Traditional DeFi</span>
                <span>Distil Protocol</span>
              </div>
              <div className="wp-compare-row">
                <span>Incentive emissions (inflationary)</span>
                <span>Regenerative fees — no emissions</span>
              </div>
              <div className="wp-compare-row">
                <span>Fixed tokenomics</span>
                <span>Adaptive (Sui Pulse)</span>
              </div>
              <div className="wp-compare-row">
                <span>Whale-dominated launches</span>
                <span>Commitment-weighted (Gravity)</span>
              </div>
              <div className="wp-compare-row">
                <span>Pool depth stagnates</span>
                <span>Auto-compounds (Engine)</span>
              </div>
              <div className="wp-compare-row">
                <span>One-size-fits-all mechanics</span>
                <span>Three engines working together</span>
              </div>
            </div>
            <p>
              Unlike pure speculation tokens, Distil has a real utility foundation — wallet cleanup solves a genuine problem, generating organic volume that feeds the engines. Copycat tokens can't replicate the utility. The three-engine system is too complex to copy quickly. First-mover advantage on Sui for this use case creates a moat that compounds over time.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Roadmap',
    emoji: '🗺️',
    items: [
      {
        id: 'q2-2026',
        title: 'Q2 2026',
        content: (
          <>
            <h1>Q2 2026</h1>
            <ul>
              <li>✅ Token Generation Event (TGE)</li>
              <li>✅ Fair Launch on Cetus DEX</li>
              <li>✅ Security audit completion</li>
              <li>✅ Public beta launch</li>
              <li>✅ Initial Distillery token launches</li>
            </ul>
          </>
        ),
      },
      {
        id: 'q3-2026',
        title: 'Q3 2026',
        content: (
          <>
            <h1>Q3 2026</h1>
            <ul>
              <li>🔄 AI Security Layer integration (Blowfish API partnership)</li>
              <li>🔄 SuiNS integration (resolve .sui names on leaderboard)</li>
              <li>🔄 Enhanced mini-games and SpinWheel rebuild</li>
              <li>🔄 Mobile PWA improvements</li>
              <li>🔄 Cross-protocol partnerships expansion</li>
              <li>🌊 Gravity Pools opens August 15 — 90-day fair launch bootstrap</li>
            </ul>
          </>
        ),
      },
      {
        id: 'q4-2026',
        title: 'Q4 2026',
        content: (
          <>
            <h1>Q4 2026</h1>
            <ul>
              <li>🌊 Gravity Pools closes November 15 — TGE on November 30</li>
              <li>💎 <strong>$DISTIL Token Generation Event (TGE)</strong> — November 30</li>
              <li>⚙️ Liquidity Engine activates at TGE</li>
              <li>📡 Sui Pulse oracle goes live</li>
              <li>🎮 Community engagement platform launch (December 2026) 👀</li>
              <li>🔜 DAO governance launch</li>
              <li>🔜 Advanced analytics dashboard</li>
              <li>🔜 Community treasury activation</li>
            </ul>
          </>
        ),
      },
      {
        id: '2027-beyond',
        title: '2027 & Beyond',
        content: (
          <>
            <h1>2027 & Beyond</h1>
            <ul>
              <li>🌐 Multi-chain expansion research</li>
              <li>🤖 Advanced AI-powered wallet analysis</li>
              <li>🏢 Enterprise partnerships</li>
              <li>🏛️ Full protocol decentralization</li>
              <li>🌱 Community-driven development roadmap</li>
            </ul>
          </>
        ),
      },
      {
        id: 'community-platform',
        title: 'Community Platform 👀',
        content: (
          <>
            <h1>Advanced Community Features (Q4 2026) 👀</h1>
            <p>
              Distil Protocol will introduce a sophisticated community engagement platform in Q4 2026 that creates new earning opportunities for ecosystem participants.
            </p>
            <div className="wp-coming-soon">
              <span className="wp-coming-soon-icon">🎮</span>
              <div>
                <strong>Community Engagement Platform — December 2026</strong>
                <p>A new layer of participation beyond wallet cleanup. Ecosystem participants will be able to engage with Sui token launches in an entirely new way.</p>
                <p>Key highlights:</p>
              </div>
            </div>
            <ul>
              <li>🚽 <strong>FREE access for Toilet Mode holders</strong> — exclusive benefit</li>
              <li>🎯 Community-driven token curation</li>
              <li>💎 Airdrop opportunities for active participants</li>
              <li>🔗 Deep integration with Sui ecosystem launches</li>
              <li>⚡ Additional <code>$DISTIL</code> utility mechanics</li>
            </ul>
            <p>
              This platform represents the next evolution of Distil Protocol — extending beyond wallet cleanup into broader ecosystem participation. Toilet Mode holders will receive premium benefits including priority access and enhanced earning potential.
            </p>
            <div className="wp-coming-soon">
              <span className="wp-coming-soon-icon">🔒</span>
              <div>
                <strong>Full details coming Q4 2026</strong>
                <p>Platform name, mechanics, and complete feature set will be announced closer to launch. The mystery is intentional — watch this space. 👀</p>
              </div>
            </div>
          </>
        ),
      },
    ],
  },
  {
    category: 'FAQ',
    emoji: '❓',
    items: [
      {
        id: 'common-questions',
        title: 'Common Questions',
        content: (
          <>
            <h1>Common Questions</h1>
            <div className="wp-faq">
              {[
                {
                  q: 'Is Distil safe to use?',
                  a: 'Yes. Distil is non-custodial — you control your private keys at all times. The protocol is professionally audited before mainnet launch. Distil never requests your seed phrase.',
                },
                {
                  q: 'How do I earn $DISTIL?',
                  a: 'Connect your wallet, scan for spam/dust objects, select what to remove, and click Purify. You earn $DISTIL rewards based on the value and quantity of objects purged in a single transaction.',
                },
                {
                  q: 'What is Toilet Mode?',
                  a: 'A premium tier unlocked by holding 287,000+ $TOILET tokens (~$10 USD). Provides 15% bonus earnings, gas rebates via $DISTIL, and exclusive features including the SpinWheel mini-game.',
                },
                {
                  q: 'When is the Token Generation Event (TGE)?',
                  a: 'November 30, 2026. Gravity Pools (the fair launch bootstrap) opens August 15 and closes November 15, with TGE following 15 days later. Follow official channels for announcements.',
                },
                {
                  q: 'How do holding bonuses work?',
                  a: 'Simply hold $DISTIL in your wallet — no staking or locking required. Bonuses automatically apply based on your balance tier (Bronze 1K+, Silver 10K+, Gold 100K+, Diamond 1M+).',
                },
                {
                  q: 'Can I lose my tokens?',
                  a: "No. Distil is non-custodial. Your tokens remain in your wallet at all times. The protocol never has custody of your assets and cannot move funds without your explicit wallet approval.",
                },
                {
                  q: 'What blockchains are supported?',
                  a: 'Sui blockchain only. Distil is Sui-native and built specifically for the Sui ecosystem.',
                },
                {
                  q: 'What if I accidentally whitelist something I want to delete?',
                  a: "Open the Whitelist Manager from the header, find the item, and click Remove. It'll return to your cleanup list immediately.",
                },
                {
                  q: 'I have multiple addresses in my wallet — how does that work?',
                  a: "Each address is tracked completely independently. Your purity score, stats, whitelist, streaks, and referrals are all tied to the specific address you connect with. If you switch to a different address, you'll see that address's own fresh stats — your progress on the first address is still there, just switch back to see it. To keep all your progress in one place, always connect the same address.",
                },
              ].map(item => (
                <div key={item.q} className="wp-faq-item">
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'troubleshooting',
        title: 'Troubleshooting',
        content: (
          <>
            <h1>Troubleshooting</h1>
            <div className="wp-faq">
              {[
                {
                  q: 'Wallet won\'t connect',
                  a: 'Clear browser cache and cookies. Try a different supported wallet (Sui Wallet, Slush, Phantom). Ensure you\'re on Sui Mainnet. Disable conflicting browser extensions.',
                },
                {
                  q: 'Objects not showing',
                  a: 'Refresh the page. Check your network connection. Verify wallet is properly connected. RPC sync may take 30–60 seconds after connection.',
                },
                {
                  q: 'Transaction failed',
                  a: 'Check your SUI balance for gas fees. Reduce batch size (purge fewer objects at once). Retry the transaction. Contact support if the issue persists.',
                },
                {
                  q: 'Whitelist not syncing',
                  a: 'Re-login to refresh the connection. Check internet connectivity. Allow up to 5 minutes for cloud sync to complete.',
                },
                {
                  q: 'Stats not updating',
                  a: 'Stats update every 5 minutes — not real-time. Refresh the browser page. Verify the transaction completed on-chain by checking the transaction hash on Sui Explorer.',
                },
              ].map(item => (
                <div key={item.q} className="wp-faq-item">
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'security',
        title: 'Security',
        content: (
          <>
            <h1>Security</h1>
            <ul>
              <li><strong>Non-custodial architecture:</strong> You control private keys at all times</li>
              <li><strong>No private key access:</strong> The protocol never requests or stores keys</li>
              <li><strong>Audit reports:</strong> Professional security audit before mainnet — links posted upon completion</li>
              <li><strong>Bug bounty:</strong> Responsible disclosure rewards for security researchers</li>
              <li><strong>Emergency contact:</strong> <a href="mailto:security@distil.wtf">security@distil.wtf</a></li>
            </ul>
          </>
        ),
      },
      {
        id: 'sui-integration',
        title: 'Sui Integration',
        content: (
          <>
            <h1>Sui Integration</h1>
            <ul>
              <li>Built on Sui blockchain using Sui Move smart contracts</li>
              <li>Integrated with <code>@mysten/dapp-kit</code> SDK for wallet connections</li>
              <li>Interacts with Sui RPC nodes via Shinami infrastructure</li>
              <li>Uses Sui native storage deposit mechanics for fee recovery</li>
              <li>Sui-native token standards for <code>$DISTIL</code></li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    category: 'Reference',
    emoji: '📖',
    items: [
      {
        id: 'terminology',
        title: 'Terminology',
        content: (
          <>
            <h1>Terminology</h1>
            <div className="wp-glossary">
              {[
                { term: 'Purity Score', def: 'Total points accumulated from purging objects. Used for badge progression and leaderboard ranking. Persists permanently.' },
                { term: 'Dust Objects', def: 'Low-value assets worth less than $0.01 USD, or tokens with zero balance. Taking up storage for no benefit.' },
                { term: 'Toxic Assets', def: 'Spam, scam tokens, malicious objects, or unwanted airdropped junk that poses security or UX risks.' },
                { term: 'Toilet Dust', def: 'Community slang for junk objects that need to be "flushed" away. All dust is toilet dust.' },
                { term: 'Purification', def: 'The act of deleting unwanted objects from your wallet to earn $DISTIL rewards. A single transaction can purify hundreds of objects.' },
                { term: 'Storage Deposit', def: 'SUI tokens locked when creating on-chain objects. Automatically reclaimed when objects are deleted via Distil.' },
                { term: 'The Distillery', def: "Distil's curated meme token launch platform. Quality-first token launches with fair distribution and community participation." },
                { term: 'Toilet Mode', def: 'Premium tier unlocked by holding $TOILET tokens. Provides 15% earnings boost, gas rebates, Dark Cosmic theme, and exclusive features.' },
                { term: 'Holding Bonus', def: 'Extra percentage rewards earned by holding $DISTIL in your wallet. No staking required. Four tiers: Bronze, Silver, Gold, Diamond.' },
                { term: 'Emission Schedule', def: 'The rate at which new $DISTIL tokens are distributed as rewards. Decreases as supply milestones are reached, not on a time calendar.' },
                { term: 'Quarantine', def: 'Isolation system for malicious objects flagged by wallet security. Quarantined objects cannot be purged — they must be reported to your wallet provider.' },
              ].map(item => (
                <div key={item.term} className="wp-gloss-item">
                  <dt>{item.term}</dt>
                  <dd>{item.def}</dd>
                </div>
              ))}
            </div>
          </>
        ),
      },
      {
        id: 'links',
        title: 'Links & Resources',
        content: (
          <>
            <h1>Official Links & Resources</h1>
            <div className="wp-links">
              {[
                { label: '🌐 Website', url: 'https://distil.wtf', display: 'distil.wtf' },
                { label: '🐦 Twitter / X', url: 'https://twitter.com/distilprotocol', display: '@distilprotocol' },
                { label: '📢 Telegram', url: 'https://t.me/distilprotocol', display: 't.me/distilprotocol' },
                { label: '💬 Discord', url: 'https://discord.gg/distil', display: 'discord.gg/distil' },
                { label: '🚽 $TOILET Community', url: 'https://flush.wtf', display: 'flush.wtf' },
              ].map(link => (
                <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer" className="wp-link-card">
                  <span className="wp-link-label">{link.label}</span>
                  <span className="wp-link-url">{link.display}</span>
                </a>
              ))}
            </div>
            <p style={{ marginTop: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
              Audit reports and GitHub links will be posted upon completion. For partnership inquiries: <a href="mailto:hello@distil.wtf">hello@distil.wtf</a>
            </p>
          </>
        ),
      },
    ],
  },
];

// ─── Main Component ────────────────────────────────────────────────────────

const Whitepaper = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = (contentRef.current?.scrollTop || 0) + 120;
      for (const cat of SECTIONS) {
        for (const item of cat.items) {
          const el = document.getElementById(`wp-${item.id}`);
          if (el) {
            const top = el.offsetTop;
            const bottom = top + el.offsetHeight;
            if (scrollPos >= top && scrollPos < bottom) {
              setActiveSection(item.id);
              break;
            }
          }
        }
      }
    };
    const ref = contentRef.current;
    ref?.addEventListener('scroll', handleScroll, { passive: true });
    return () => ref?.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`wp-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
      setMobileNavOpen(false);
    }
  };

  return (
    <div className="wp-overlay" onClick={onClose}>
      <div className="wp-modal" onClick={e => e.stopPropagation()}>

        {/* Header bar */}
        <div className="wp-header">
          <div className="wp-header-left">
            <span className="wp-header-icon">🚽</span>
            <div>
              <h1 className="wp-header-title">Distil Protocol</h1>
              <span className="wp-header-sub">Documentation & Whitepaper</span>
            </div>
          </div>
          <div className="wp-header-right">
            <button
              className="wp-mobile-nav-btn"
              onClick={() => setMobileNavOpen(v => !v)}
            >
              {mobileNavOpen ? '✕ Close' : '☰ Contents'}
            </button>
            <button className="wp-close-btn" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        <div className="wp-body">
          {/* Sidebar nav */}
          <nav className={`wp-nav${mobileNavOpen ? ' open' : ''}`}>
            <div className="wp-nav-inner">
              {SECTIONS.map(cat => (
                <div key={cat.category} className="wp-nav-cat">
                  <div className="wp-nav-cat-title">
                    <span className="wp-nav-cat-emoji">{cat.emoji}</span>
                    {cat.category}
                  </div>
                  <ul>
                    {cat.items.map(item => (
                      <li
                        key={item.id}
                        className={`wp-nav-item${activeSection === item.id ? ' active' : ''}`}
                        onClick={() => scrollTo(item.id)}
                      >
                        {item.title}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          {/* Content */}
          <main className="wp-content" ref={contentRef}>
            {SECTIONS.map(cat => (
              <div key={cat.category}>
                {cat.items.map(item => (
                  <section
                    key={item.id}
                    id={`wp-${item.id}`}
                    className="wp-section"
                  >
                    {item.content}
                  </section>
                ))}
              </div>
            ))}
            <div className="wp-footer">
              <p>Distil Protocol — The Sanitation Department of Sui</p>
              <p>v1.1 · Updated April 2026 · <a href="mailto:hello@distil.wtf">hello@distil.wtf</a></p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Whitepaper;
